import React, { useState } from 'react';
import type { EntryPointProps } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import LoadingButton from '@atlaskit/button/loading-button';
import { ModalBody, ModalHeader, ModalTitle, ModalFooter } from '@atlaskit/modal-dialog';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	MountEvent,
	MODAL,
	fireScreenAnalyticsDeferred,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useRouterActions } from '@atlassian/react-resource-router';
import messages from './messages.tsx';
import type { NavigationDiscardChangesPromptProps } from './types.tsx';

export const NavigationDiscardChangesPrompt = ({
	navigateLink,
	onConfirm,
	onCancel,
}: NavigationDiscardChangesPromptProps) => {
	const [isNavigating, setIsNavigating] = useState<boolean>(false);
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onCancelHandler = (_: any, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'cancelNavigationDisardChangesPrompt', {
			navigateLink,
		});
		onCancel?.();
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onConfirmHandler = (_: any, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'confirmNavigationDisardChangesPrompt', {
			navigateLink,
		});
		onConfirm && onConfirm();
		setIsNavigating(true);
		push(navigateLink);
	};

	if (!navigateLink) {
		return null;
	}

	return (
		<ContextualAnalyticsData sourceType={MODAL} sourceName="navigateWithoutRestore">
			<MountEvent onMount={fireScreenAnalyticsDeferred} />
			<ShortcutScope>
				<ModalDialog
					onClose={onCancelHandler}
					width="small"
					shouldCloseOnOverlayClick={!isNavigating}
					shouldCloseOnEscapePress={!isNavigating}
					testId="issue-create.common.ui.navigation-discard-changes-prompt.modal-dialog"
					messageId="issue-create.common.ui.navigation-discard-changes-prompt.modal-dialog"
					messageType="transactional"
				>
					<ModalHeader>
						<ModalTitle>{formatMessage(messages.title)}</ModalTitle>
					</ModalHeader>
					<ModalBody>{formatMessage(messages.description)}</ModalBody>

					<ModalFooter>
						<Button
							testId="issue-create.common.ui.navigation-discard-changes-prompt.button"
							appearance="subtle-link"
							onClick={onCancelHandler}
							isDisabled={isNavigating}
						>
							{formatMessage(messages.cancelButtonLabel)}
						</Button>
						<LoadingButton
							testId="issue-create.common.ui.navigation-discard-changes-prompt.loading-button"
							appearance="primary"
							onClick={onConfirmHandler}
							isDisabled={isNavigating}
							isLoading={isNavigating}
						>
							{formatMessage(messages.confirmLabel)}
						</LoadingButton>
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
		</ContextualAnalyticsData>
	);
};

export default function NavigationDiscardChangesEntryPoint({
	props,
}: EntryPointProps<{}, {}, NavigationDiscardChangesPromptProps, {}>) {
	return <NavigationDiscardChangesPrompt {...props} />;
}
